import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';

// PROD-7828 E1: who asked for a session change and when. Pure helpers shared by every Care change flow; the goserver
// endpoint re-checks all of it. Times are entered and shown in IST and sent as UTC ISO strings.

export const IST = 'Asia/Kolkata';
export const KIND_RESCHEDULE = 'RESCHEDULE';
export const KIND_CANCEL = 'CANCEL';
export const KIND_TEACHER_CHANGE = 'TEACHER_CHANGE';

export const INITIATOR_OPTIONS = [
  { value: 'TEACHER', label: 'Teacher' },
  { value: 'STUDENT', label: 'Student' }
];

export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
const CLOCK_SKEW_MS = 2 * 60 * 1000;
const NOTE_MIN = 3;
const INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

// Mirrors goserver carereq.oneOnOneTypes: the session types this flow records for.
const ONE_ON_ONE_TYPES = [
  '',
  'YOGA',
  'VIDEO_CONSULT',
  'CONSULTATION',
  'BACK_PAIN_CONSULT',
  'BACK_PAIN_THERAPY',
  'YOGA_WITH_FRIENDS_SESSION'
];

export const is_one_on_one = (session_type) => ONE_ON_ONE_TYPES.includes(session_type || '');

export const KIND_LABELS = {
  [KIND_RESCHEDULE]: 'Reschedule',
  [KIND_CANCEL]: 'Cancellation',
  [KIND_TEACHER_CHANGE]: 'Teacher change'
};

// datetime-local value of an instant, in IST.
export const ist_input_value = (ms) => moment(ms).tz(IST).format(INPUT_FORMAT);

// datetime-local value read as IST, as a UTC ISO string ('' when empty or malformed).
export function ist_input_to_iso(value) {
  if (!value) return '';
  const m = moment.tz(value, INPUT_FORMAT, true, IST);
  return m.isValid() ? m.toISOString() : '';
}

export const format_ist = (iso) =>
  iso ? `${moment(iso).tz(IST).format('DD MMM YYYY, hh:mm A')} IST` : '--';

// A fresh draft; the uuid is the save's idempotency key, so a retry after a lost reply cannot save twice.
// Teacher changes default "requested at" to the swap time (editable) and are always the teacher's request.
export function new_care_draft(kind, now_ms) {
  const is_swap = kind === KIND_TEACHER_CHANGE;
  return {
    uuid: uuidv4(),
    kind,
    initiated_by: is_swap ? 'TEACHER' : '',
    requested_at: is_swap ? ist_input_value(now_ms) : '',
    note: '',
    file: null
  };
}

// Field errors ({} = valid). initiated_by and note are checked only when this form owns them.
export function validate_care_draft(
  draft,
  { now_ms, require_initiated_by = true, require_note = false }
) {
  const errors = {};
  if (require_initiated_by && !INITIATOR_OPTIONS.some((o) => o.value === draft.initiated_by)) {
    errors.initiated_by = 'Choose who asked for the change.';
  }
  const iso = ist_input_to_iso(draft.requested_at);
  if (!iso) {
    errors.requested_at = 'Enter when the teacher or student asked (IST).';
  } else if (moment(iso).valueOf() > now_ms + CLOCK_SKEW_MS) {
    errors.requested_at = 'The request time is in the future.';
  }
  if (require_note && (draft.note || '').trim().length < NOTE_MIN) {
    errors.note = 'A note (at least 3 characters) is required.';
  }
  if (draft.file) {
    if (!SCREENSHOT_TYPES.includes(draft.file.type)) {
      errors.file = 'The screenshot must be a PNG, JPG, GIF or WEBP image.';
    } else if (draft.file.size > SCREENSHOT_MAX_BYTES) {
      errors.file = 'The screenshot is larger than 5 MB.';
    }
  }
  return errors;
}

export const has_errors = (errors) => Object.keys(errors || {}).length > 0;

// Body of POST /v2/cms_user/session_care_requests. note/initiated_by may come from the host form (the apis note, the
// cancel radio) so the operator types each fact once. expected_* = what this user's own change set, so the server
// never attaches the details to another user's simultaneous change.
export function build_create_payload(
  draft,
  {
    session_uuid,
    note,
    initiated_by,
    screenshot_key,
    change_ref,
    expected_start_time,
    expected_teacher_uuid
  }
) {
  const payload = {
    uuid: draft.uuid,
    session_uuid,
    change_kind: draft.kind,
    initiated_by: initiated_by || draft.initiated_by,
    requested_at: ist_input_to_iso(draft.requested_at),
    note: (note !== undefined ? note : draft.note || '').trim()
  };
  if (screenshot_key) payload.screenshot_key = screenshot_key;
  if (change_ref) payload.change_ref = change_ref;
  if (expected_start_time) payload.expected_start_time = expected_start_time;
  if (expected_teacher_uuid) payload.expected_teacher_uuid = expected_teacher_uuid;
  return payload;
}

// A refusal as {code, message, existing_uuid}; the module envelope carries error_code || reason.
export function care_api_error(e, fallback) {
  const d = e?.response?.data || {};
  const code = d.error_code || (typeof d.reason === 'string' ? d.reason : '');
  const message = typeof d.message === 'string' && d.message ? d.message : fallback;
  return { code, message, existing_uuid: d.existing_uuid || '' };
}

// The session page's Edit modal records a cancellation only when it cancels a live 1-on-1 (not an edit of an
// already-cancelled session).
export const needs_cancel_capture = ({ status, original_was_cancelled, session_type }) =>
  status === 'CANCELLED' && !original_was_cancelled && is_one_on_one(session_type);

// The cancel radios are the initiator: Cancelled by Teacher -> TEACHER, by Student -> STUDENT.
export function cancel_initiator(cancelled_by_teacher, cancelled_by_student) {
  if (cancelled_by_teacher) return 'TEACHER';
  if (cancelled_by_student) return 'STUDENT';
  return '';
}
